<script setup>
import { ref, computed, onMounted } from 'vue'

import { useUserStore } from '@/stores/userStore'
import router from '@/router'
import AccountHeader from '@/components/AccountHeader.vue'
import { useDisplay } from 'vuetify'
import { changePassword, changePasswordAdmin } from '@/services/authSignin'
import logoSodaSoft from '@/assets/logo.svg'
import profileImg from '@/views/profile.jpg'

import { useRoute } from 'vue-router'
const route = useRoute()
const routeHide = ref(route.name)
const { mdAndDown } = useDisplay()

const userStore = useUserStore()
const logoutDialog = ref(false)
const showDialog = ref(false)
const currentPassword = ref('')
const newPassword = ref('')
const showCurrent = ref(false)
const showNew = ref(false)
const showNewConfirm = ref(false)
const newPasswordConfirm = ref('')
const loading = ref(false)
const snackbar = ref({
  show: false,
  message: '',
  color: 'info',
})

import Cookies from 'universal-cookie'
import { LogoutFunc } from '@/api/accountsAdmin'
import { LogoutFuncUser } from '@/api/accountsClient'

const cookies = new Cookies()
const user = cookies.get('user')
const admin = cookies.get('admin')

const drawer = ref(true)

function updateDrawer() {
  drawer.value = window.innerWidth >= 1200
}

  onMounted(() => {
    updateDrawer()
    window.addEventListener('resize', updateDrawer)
  })

  onBeforeUnmount(() => {
    window.removeEventListener('resize', updateDrawer)
  })
onMounted(() => {
  if (userStore.isPasswordChanged === '0') {
    showDialog.value = true
  }
})
const logout = async () => {
  const logoutDialog = ref(false)

  try {
    if (
      userStore?.role === 'client_user' ||
      userStore?.role === 'client_manager' ||
      userStore?.role === 'client_admin'
    ) {
      await LogoutFuncUser()
      snackbar.value = { show: true, message: 'User logged out successfully', color: 'success' }
    } else if (
      userStore?.role === 'company_user' ||
      userStore?.role === 'company_admin' ||
      userStore?.role === 'company_suadmin' ||
      userStore?.role === 'company_owner'
    ) {
      await LogoutFunc()
      snackbar.value = { show: true, message: 'User logged out successfully', color: 'success' }
    }

    setTimeout(() => {
      logoutDialog.value = false
      userStore.$reset()
      router.push('/')
      localStorage.clear()
    }, 2000)
  } catch (error) {
    logoutDialog.value = false
    userStore.$reset()
    router.push('/')
    localStorage.clear()
  }

  setTimeout(() => {
    logoutDialog.value = false
    userStore.$reset()
    router.push('/')
  }, 2000)
}

const cookie = new Cookies()
const userRole = ref(cookie.get('admin')?.role) // "company_user", etc.

const allItems = [
  { title: 'Accounts', to: '/account-admin', icon: 'mdi-store' },
  { title: 'Products', to: '/products', icon: 'mdi-file-document' },
  { title: 'Company Users', to: '/company-accounts', icon: 'mdi-account' },
  { title: 'Client Users', to: '/client-accounts', icon: 'mdi-account' },
  { title: 'Activity Logs', to: '/activity-logs', icon: 'mdi-message-text' },
]
const navItems = computed(() => {
  let filteredItems = allItems

  // Filter based on user role
  if (userStore.role === 'company_user') {
    // Regular company users can't see Company Users, Client Users, or Activity Logs
    filteredItems = filteredItems.filter(
      (i) =>
        i.title !== 'Company Users' && i.title !== 'Client Users' && i.title !== 'Activity Logs'
    )
  } else if (userStore.role === 'company_admin') {
    // Company admins can see everything except Activity Logs
    filteredItems = filteredItems.filter((i) => i.title !== 'Activity Logs')
  } else if (userStore.role === 'company_suadmin' || userStore.role === 'company_owner') {
    // Super Admin and Owner can see everything including Activity Logs
    filteredItems = allItems
  } else {
    // For any other roles, filter out Activity Logs
    filteredItems = filteredItems.filter((i) => i.title !== 'Activity Logs')
  }

  return filteredItems
})

const items = computed(() => {
  const isClient =
    userStore?.role === 'client_admin' ||
    userStore?.role === 'client_manager' ||
    userStore?.role === 'client_user'
  if (isClient) {
    const baseItems = [
      {
        title: 'Overview',
        to: `/account/${userStore?.juid}#info`,
        icon: 'mdi-information-outline',
      },
      {
        title: 'Products',
        to: `/account/${userStore?.juid}#products`,
        icon: 'mdi-package-variant',
      },
    ]
    if (userStore?.role === 'client_admin') {
      baseItems.push({ title: 'Users', to: '/users', icon: 'mdi-account' })
    }
    return baseItems
  }

  // Admin context-aware items: when on /account-admin/:name show anchor shortcuts
  const onAdminAccount = route.path.startsWith('/account-admin/') && !!route.params.name
  if (onAdminAccount) {
    // When viewing a specific account (especially on mobile), we keep standard nav
    // but will render shortcut links to Info/Products additionally below the list.
    // So here we just fall through to default nav items.
  }

  // Default admin navigation
  return navItems.value
})

// Mobile helper: show account-level shortcuts in drawer when on a specific account
const onAdminAccountMobile = computed(() => {
  return mdAndDown.value && route.path.startsWith('/account-admin/') && !!route.params.name
})

// Mobile helper: show product-level shortcuts in drawer when on a specific product
const onProductMobile = computed(() => {
  return mdAndDown.value && route.path.startsWith('/products/view/') && !!route.params?.id
})

// Friendly role label for header chip
const roleLabel = computed(() => {
  const r = userStore?.role || ''
  switch (r) {
    case 'company_owner':
      return 'Owner'
    case 'company_suadmin':
      return 'Super Admin'
    case 'company_admin':
      return 'Admin'
    case 'company_user':
      return 'User'
    case 'client_admin':
      return 'Client Admin'
    case 'client_manager':
      return 'Client Manager'
    case 'client_user':
      return 'Client User'
    default:
      return r || '—'
  }
})

async function onChange() {
  if (!currentPassword.value || !newPassword.value || !newPasswordConfirm) {
    snackbar.value = { show: true, message: 'All fields are required', color: 'error' }
    return
  }

  if (!currentPassword.value || !newPassword.value || !newPasswordConfirm.value) {
    snackbar.value = { show: true, message: 'All fields are required', color: 'error' }
    return
  }
  if (newPasswordConfirm.value !== newPassword.value) {
    snackbar.value = {
      show: true,
      message: '❌ New password and confirmation do not match.',
      color: 'error',
    }
    loading.value = false

    return
  }
  loading.value = true
  try {
    if (
      userStore?.role === 'client_user' ||
      userStore?.role === 'client_manager' ||
      userStore?.role === 'client_admin'
    ) {
      await changePassword(currentPassword.value, newPassword.value)
      console.log('client pool hit ')
      snackbar.value = { show: true, message: 'Password changed!', color: 'success' }
      showDialog.value = false
      currentPassword.value = newPassword.value = ''
    } else if (
      userStore?.role === 'company_user' ||
      userStore?.role === 'company_admin' ||
      userStore?.role === 'company_suadmin' ||
      userStore?.role === 'company_owner'
    ) {
      await changePasswordAdmin(currentPassword.value, newPassword.value)
      console.log('company pool hit ')

      snackbar.value = { show: true, message: 'Password changed!', color: 'success' }
      showDialog.value = false
      currentPassword.value = newPassword.value = ''
    }
  } catch (err) {
    snackbar.value = {
      show: true,
      message: err.message || 'Failed to change password',
      color: 'error',
    }
  } finally {
    loading.value = false
  }
}
const AllRoute = [
  {
    path: `/account-admin`,
    prevNavLink: ``,
  },
  {
    path: `/account/:name`,
    prevNavLink: ``,
  },
  {
    path: `/account-admin/:name`,
    prevNavLink: `/account-admin`,
  },
  {
    path: `/account/edit/:name`,
    prevNavLink: `/account/:name`,
  },
]
import { useBackLink } from '@/views/useBackLink.js'
import { onBeforeUnmount } from 'vue'
const { backLink } = useBackLink()
const showBackButton = computed(() => {
  const allowedRoles = ['company_user', 'company_admin', 'company_suadmin', 'company_owner']

  return allowedRoles.includes(userStore.role) && route.name === 'CreateUserClient'
})

const showBackButtonUser = computed(() => {
  const allowedRoles = ['client_admin']

  return allowedRoles.includes(userStore.role) && route.name === 'CreateUserClient'
})

const showBackButtonAdmin = computed(() => {
  const allowedRoles = ['company_user', 'company_admin', 'company_suadmin', 'company_owner']

  return (
    (allowedRoles.includes(userStore.role) && route.name === 'AddProduct') ||
    route.name === 'AllProductsAccounts' ||
    route.name === 'EditAccountViewAdmin'
  )
})

// Determine active state for nav items, including hash-aware links
function isItemActive(item) {
  const to = item?.to || ''
  if (to.includes('#')) {
    const parts = to.split('#')
    const path = parts[0]
    const hash = `#${parts[1] || ''}`
    return route.path === path && route.hash === hash
  }
  // Highlight Accounts when on any /account-admin/... path
  if (to === '/account-admin')
    return (
      route.path.startsWith('/account-admin') ||
      route.path.startsWith('/account/products') ||
      route.path.startsWith('/admin/add-account')
    )
  // Highlight Company Accounts when on any /company-accounts/... path
  if (to === '/company-accounts') return route.path.startsWith('/company-accounts')
  // Highlight client Accounts when on any /account/... path
  if (to === '/account') return route.path.startsWith('/account/')
  // Highlight Products when on any /products/... path
  if (to === '/products') return route.path.startsWith('/products')
  return route.path === to
}
</script>

<template>
  <v-app>
    <v-layout class="rounded rounded-md" style="height: 100vh">
      <!-- App Bar with Hamburger Icon for Drawer -->
      <v-app-bar app>
        <v-app-bar-nav-icon
          @click.stop="drawer = !drawer"
          v-if="
            [
              'client_admin',
              'client_manager',
              'client_user',
              'company_user',
              'company_admin',
              'company_suadmin',
              'company_owner',
            ].includes(userStore?.role)
          "
        />

        <v-app-bar-title>
          <div class="d-flex align-center" style="gap: 10px">
            <img
              :src="logoSodaSoft"
              alt="SodaSoft"
              style="height: 48px; width: 48px; object-fit: contain"
            />
            <span>{{ $vuetify.display.mdAndDown ? 'JAMS' : 'Jazel Account Manager' }}</span>
          </div>
        </v-app-bar-title>

        <div class="d-none d-sm-flex align-center" style="margin-right: 10px; gap: 10px">
          <span>Hi, {{ userStore?.username }}</span>
          <v-chip size="small" color="grey" text-color="grey-darken-3" variant="tonal">
            {{ roleLabel }}
          </v-chip>
        </div>

        <v-menu offset-y>
          <template #activator="{ props }">
            <v-btn style="margin-right: 10px" v-bind="props" icon>
              <v-avatar size="35">
                <img :src="profileImg" width="40" height="40" alt="Profile" />
              </v-avatar>
            </v-btn>
          </template>
          <v-list>
            <v-list-item>
              <v-list-item-title class="d-flex align-center" style="gap: 10px">
                {{ userStore?.username }}
                <v-chip size="small" color="grey" text-color="grey-darken-3" variant="tonal">
                  {{ roleLabel }}
                </v-chip>
              </v-list-item-title>
            </v-list-item>
            <v-divider />

            <v-list-item :to="{ name: 'WordPressSSO' }">
              <v-icon left>mdi-wordpress</v-icon>
              WordPress SSO
            </v-list-item>

            <v-list-item @click="showDialog = true">
              <v-icon left>mdi-lock-reset</v-icon>

              Change Password
            </v-list-item>

            <v-list-item style="display: flex" @click="logoutDialog = true">
              <v-icon>mdi-logout</v-icon>
              Logout
            </v-list-item>
          </v-list>
        </v-menu>
      </v-app-bar>

      <v-navigation-drawer v-model="drawer" :permanent="!mdAndDown" :temporary="mdAndDown" app>
        <v-list>
          <v-list-item
            v-for="(item, index) in userStore.role === 'client_admin' ||
            userStore.role === 'client_manager' ||
            userStore.role === 'client_user'
              ? items
              : navItems"
            :key="index"
            :to="item.to"
            link
            :prepend-icon="item.icon"
            :active="isItemActive(item)"
          >
            <template #title>{{ item.title }}</template>
          </v-list-item>
        </v-list>
      </v-navigation-drawer>

      <!-- Main Scrollable Content -->
      <v-main style="overflow-y: auto; overflow-x: scroll">
        <AccountHeader v-if="$route.meta.showAccountHeader" />
        <router-view :key="$route.fullPath" />
      </v-main>
    </v-layout>

    <!-- Logout Confirmation Dialog -->
    <v-dialog v-model="logoutDialog" max-width="400">
      <v-card title="Confirm Logout">
        <v-card-text>Are you sure you want to log out?</v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn text="Cancel" @click="logoutDialog = false"></v-btn>
          <v-btn color="red" text="Confirm Logout" @click="logout"></v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <v-dialog v-model="showDialog" max-width="400px">
      <v-card>
        <v-card-title class="headline">Change Password</v-card-title>
        <p
          style="
            margin-left: 25px;
            margin-right: 25px;
            padding: 6px 9px;
            background-color: red;
            color: white;
            border-radius: 5px;
          "
          v-if="userStore.isPasswordChanged === '0'"
        >
          Please Change Your Password
        </p>
        <v-card-text>
          <div class="input-div">
            <!-- Current Password -->
            <div class="password-wrapper">
              <input
                :type="showCurrent ? 'text' : 'password'"
                v-model="currentPassword"
                placeholder="Current Password"
                class="input"
              />
              <button type="button" class="eye-btn" @click="showCurrent = !showCurrent">
                <i :class="showCurrent ? 'mdi mdi-eye-off' : 'mdi mdi-eye'"></i>
              </button>
            </div>

            <!-- New Password -->
            <div class="password-wrapper">
              <input
                :type="showNew ? 'text' : 'password'"
                v-model="newPassword"
                placeholder="New Password"
                class="input"
              />
              <button type="button" class="eye-btn" @click="showNew = !showNew">
                <i :class="showNew ? 'mdi mdi-eye-off' : 'mdi mdi-eye'"></i>
              </button>
            </div>
            <div class="password-wrapper">
              <input
                :type="showNewConfirm ? 'text' : 'password'"
                v-model="newPasswordConfirm"
                placeholder="Confirm New Password"
                class="input"
              />
              <button type="button" class="eye-btn" @click="showNewConfirm = !showNewConfirm">
                <i :class="showNeConfirm ? 'mdi mdi-eye-off' : 'mdi mdi-eye'"></i>
              </button>
            </div>
          </div>
        </v-card-text>
        <v-card-actions>
          <v-spacer />
          <v-btn text @click="showDialog = false">Cancel</v-btn>
          <v-btn color="primary" @click="onChange" :loading="loading">Submit</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <v-snackbar v-model="snackbar.show" :color="snackbar.color" timeout="3000">
      {{ snackbar.message }}
      <template v-slot:action="{ attrs }">
        <v-btn text v-bind="attrs" @click="snackbar.show = false">Close</v-btn>
      </template>
    </v-snackbar>
  </v-app>
</template>

<style scoped>
.input-div {
  display: flex;
  flex-direction: column;
  gap: 1rem;
}

.login-btn {
  border-radius: 8px;
  box-shadow: none;
  font-weight: 400 !important;
  text-transform: capitalize;
  letter-spacing: 0.1px;
}

.input {
  width: 100%;
  height: 42px;
  padding: 0 1rem;
  border: 1px solid #bdbcbc;
  border-radius: 8px;
}

.password-wrapper {
  position: relative;
}

.eye-btn {
  position: absolute;
  top: 50%;
  right: 0.8rem;
  transform: translateY(-50%);
  background: transparent;
  border: none;
  cursor: pointer;
}

.eye-btn .mdi {
  font-size: 1.2em;
}

.hide-scroll {
  scrollbar-width: none;
  -ms-overflow-style: none;
}

.hide-scroll {
  scrollbar-width: none;
  -ms-overflow-style: none;
}

.hide-scroll::-webkit-scrollbar {
  display: none;
}

.v-main {
  overflow-x: auto;
  width: 100%;
}
.v-app-bar {
  padding: 0px 20px;
}

@media (max-width: 1300px) {
}

@media (max-width: 800px) {
}
</style>
